import { VegaLiteSpec } from './conversationalAnalytics';

export interface AskQuestionInput {
  question: string;
  sessionId?: string;
  timezone?: string;
}

export interface Answer {
  answer: string;
  sessionId: string;
}

export interface RateAnswerInput {
  sessionId: string;
  answerIndex: number;
  rating?: string;
  feedback?: string;
}

export interface Rating {
  status: string;
}

export interface AIChatThread {
  id: string;
  title?: string;
  metadata?: Record<string, any>;
  createdDate: number;
  updatedDate: number;
}

export interface AIChatThreadConnection {
  totalCount: number;
  threads: AIChatThread[];
}

export interface AIChatThreadConnectionInput {
  agentId?: string;
  page?: number;
  limit?: number;
}

export interface AIChatThreadCreateInput {
  agentId?: string;
  threadId?: string;
}

export interface AIChatThreadUpdateInput {
  threadId: string;
  agentId?: string;
  title?: string;
  metadata?: Record<string, any>;
}

export interface AIChatMessage {
  id: string;
  role: string;
  metadata?: Record<string, any>;
  parts: Record<string, any>[];
}

export interface AIChatMessageConnection {
  totalCount: number;
  messages: AIChatMessage[];
}

export interface AIChatMessageConnectionInput {
  threadId: string;
  agentId?: string;
  page?: number;
  limit?: number;
}

export interface ChatEntry {
  question: string;
  answer: string;
  rating?: string | null;
  feedback?: string | null;
  timestamp: number;
}

export enum AIChatToolTypes {
  QUERY_CONVERSATIONAL_ANALYTICS = 'query_conversational_analytics',
  // no control over the naming here, hence the non-snakecase name :(
  ASSET_DRAFT_FROM_PHOTO = 'workflow-assetDraftFromPhotoWorkflow',
}

export enum AIChatWorkflows {
  ASSET_DRAFT_FROM_PHOTO = 'asset-draft-from-photo-workflow',
}

export type AIChatMessageFeedbackSentiment = 'positive' | 'negative';

export interface AIChatMessageFeedback {
  id: string;
  threadId: string;
  messageId: string;
  resourceId: string;
  sentiment: AIChatMessageFeedbackSentiment;
  createdDate: number;
  updatedDate: number;
}

export interface AIChatMessageFeedbackConnection {
  totalCount: number;
  feedback: AIChatMessageFeedback[];
}

export interface AIChatMessageFeedbackConnectionInput {
  threadId: string;
}

export interface AIChatMessageFeedbackUpsertInput {
  threadId: string;
  messageId: string;
  sentiment: string;
  comment?: string;
}

export enum AIChatToolIds {
  UPDATE_WORKING_MEMORY = 'updateWorkingMemory', // owned by mastra and doesn't follow snake_case paradigm :(
  QUERY_CONVERSATIONAL_ANALYTICS = 'query_conversational_analytics',
  QUERY_ENTITY_RAG = 'query_entity_rag',
  SET_EXPORT_METADATA = 'set_export_metadata',
}

export enum AIChatDataTypes {
  CONVERSATIONAL_ANALYTICS_QUERY = 'conversational-analytics-query',
  CONVERSATIONAL_ANALYTICS_SQL = 'conversational-analytics-sql',
  CONVERSATIONAL_ANALYTICS_RESULT = 'conversational-analytics-result',
  CONVERSATIONAL_ANALYTICS_CHART = 'conversational-analytics-chart',
  CONVERSATIONAL_ANALYTICS_STATUS = 'conversational-analytics-status',
  TOOL_CALL_SUSPENDED = 'tool-call-suspended',
}

export type AIChatDataPart<T = any> = {
  type: AIChatDataTypes | string;
  data: T;
};

export type AIChatToolCall<T = any> = {
  toolName: AIChatToolIds | string;
  input: T;
};

export enum AssetDraftFromPhotoWorkflowStep {
  EXTRACT_PHOTO = 'extract-photo',
  PARSE_EXIF = 'parse-exif',
  FETCH_SCHEMAS = 'fetch-schemas',
  GEOCODE_ADDRESS = 'geocode-address',
  MATCH_SCHEMA = 'match-schema',
  FETCH_SCHEMA_DETAIL = 'fetch-schema-detail',
  FETCH_ASSET_PATHS = 'fetch-asset-paths',
  MATCH_CATEGORY = 'match-category',
  FILL_ATTRIBUTES = 'fill-attributes',
  BUILD_RESULT = 'build-result',
}

export enum AIChatStatuses {
  THINKING = 'Thinking',
  ANALYZING_QUESTION = 'Analyzing the question',
  CONSTRUCTING_QUERY = 'Constructing the question into a query',
  QUERYING = 'Querying the database',
  ANALYZING_RESULTS = 'Analyzing the results',
  EXTRACTING_PHOTO = 'Extracting photo details',
  FINDING_SCHEMA = 'Finding matching inventory type',
  FINDING_CATEGORY = 'Finding matching category',
  FILLING_ATTRIBUTES = 'Filling attributes',
  PREPARING_DRAFT = 'Preparing draft asset',
}

export type AIChatToolExportCreateInput = {
  threadId: string;
  messageId: string;
  tool: {
    toolCallId: string;
    toolName: string;
    metadata?: any;
  };
};

export type AIChatToolExport = {
  fileName: string;
  url: string;
};

export type AIConversationalAnalyticsQueryResult = {
  text: string;
  sql: string;
  result: any;
  chart?: {
    vegaConfig: VegaLiteSpec;
  };
};

type GeoLocation = {
  type: string;
  coordinates: number[];
};

export type DraftElement = {
  name: string;
  schemaId: string;
  attributes: Record<
    string,
    {
      value: string | number | boolean | null;
      confidence: 'high' | 'medium' | 'low';
      reason: string;
    }
  >;
  elements: DraftElement[];
};

export type DraftAsset = {
  name: string;
  schemaId: string;
  categoryId: string;
  address: string | null;
  location: GeoLocation | null;
  attributes: Record<
    string,
    {
      value: string | number | boolean | null;
      confidence: 'high' | 'medium' | 'low';
      reason: string;
    }
  >;
  elements: DraftElement[];
};

export type CreatedElement = {
  id: string;
  name: string;
  schemaId: string;
  attributes: Record<string, { value: unknown }>;
  elements: CreatedElement[];
};

export type CreatedAsset = {
  id: string;
  name: string;
  schemaId: string;
  categoryId: string;
  address: string | null;
  location: GeoLocation | null;
  attributes: Record<string, { value: unknown }>;
  elements?: CreatedElement[];
  createdAt: string;
};

export type AssetDraftFromPhotoResult = {
  draftAsset: DraftAsset;
  asset: CreatedAsset | null;
};

export type AssetDraftFromPhotoToolOutput = {
  result: AssetDraftFromPhotoResult;
  runId: string;
};

export enum MastraWorkflowSuspensionTypes {
  ASSET_DRAFT_FROM_PHOTO_WORKFLOW_MATCH_SCHEMA = 'asset-draft-from-photo-workflow:match-schema',
  ASSET_DRAFT_FROM_PHOTO_WORKFLOW_MATCH_CATEGORY = 'asset-draft-from-photo-workflow:match-category',
}

export type SuspendedToolCallCandidate = {
  id: string;
  name: string;
};

export type SuspendedToolCallData = {
  state: 'input-available';
  runId: string;
  toolCallId: string;
  toolName: string;
  suspendPayload: {
    type: MastraWorkflowSuspensionTypes;
    prompt: string;
    candidates: SuspendedToolCallCandidate[];
  };
};

export type ResumeData = {
  candidateId?: string;
  message: string;
};

export type AIChatModel = {
  id: string;
  name: string;
};

export type AIChatModels = {
  default: AIChatModel;
  models: AIChatModel[];
};

export type AIChatAttachment = {
  mediaId: string;
  mimeType: string;
  fileId: string;
  name: string;
  status: 'uploading' | 'ready' | 'error';
};
